import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Box, Divider, Flex, Grid, Group, Text, TextInput, UnstyledButton} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import AntibodyChainLegend from 'antibodies/antibody-map/AntibodyChainLegend';
import {
  VeritasFormatPreviewQuery,
  VeritasFormatPreviewQueryVariables,
} from 'antibodies/format/__generated/VeritasFormatPreviewQuery';
import * as AntibodyFormatDAO from 'antibodies/format/AntibodyFormatDAO';
import {VeritasHelpGuideModal} from 'antibodies/format/help-modal/VeritasHelpGuideModal';
import {
  useVeritasFormatPreviewQuery,
  VERITAS_FORMAT_PREVIEW_QUERY,
} from 'antibodies/format/useVeritasFormatPreviewQuery';
import VeritasHighlightedInput, {VeritasHighlightedText} from 'antibodies/format/VeritasHighlightedInput';
import VeritasSyntaxKey from 'antibodies/format/VeritasSyntaxKey';
import {canUseBiologicsFeature} from 'antibodies/util/biologicsCapability';
import Antibody from 'antibodies/visualization/Antibody';
import {BiologicsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {FONT_PROXIMA_12, FONT_PROXIMA_16, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import legacyRunModal from 'ui-library/legacyRunModal';
import {CenteredSpinner} from 'ui-library/Spinner';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

export interface AntibodyFormatCreationPaneProps {
  onRequestClose: () => void;
  onCreate?: (formatId: string) => Promise<void>;
  analyticsMetadata: AnalyticsMetadataForContext;
  /** When provided, the pane opens in edit mode with fields pre-populated and VERITAS name read-only. */
  existingFormat?: AntibodyFormatGraphQL;
  /** Called after a successful display name update. */
  onDisplayNameUpdated?: () => Promise<void>;
}

export const EXAMPLE_FORMATS = ['scFv-Fc', 'scFv!-heteroFc-F', '[VH:VL*scFv]-heteroFc'];

function ExampleFormatCard({veritasName, onClick}: {veritasName: string; onClick: () => void}): JSX.Element {
  const queryResult = useSkippableBenchlingQuery<
    VeritasFormatPreviewQuery,
    VeritasFormatPreviewQueryVariables
  >(VERITAS_FORMAT_PREVIEW_QUERY, {
    variables: {veritasFormatName: veritasName},
    fetchPolicy: 'no-cache',
  });
  const antibodyFormat = useMemo(() => {
    if (
      queryResult.type === QueryResultType.SUCCESS &&
      queryResult.data.veritasFormatPreview?.__typename === 'AntibodyFormatNode'
    ) {
      return queryResult.data.veritasFormatPreview;
    }
    return null;
  }, [queryResult]);

  return (
    <UnstyledButton onClick={onClick} sx={{flex: 1}} aria-label={veritasName}>
      <Flex
        direction="column"
        align="center"
        gap="6px"
        p="8px"
        bg={Color.GRAY_50}
        sx={{
          border: `1px solid ${Color.GRAY_200}`,
          borderRadius: '4px',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: Color.GRAY_100,
            borderColor: Color.GRAY_300,
          },
        }}
      >
        <Flex justify="center" align="center" w="72px" h="72px">
          {antibodyFormat != null && (
            <Antibody format={antibodyFormat} showLabels={false} showPopovers={false} />
          )}
        </Flex>
        <VeritasHighlightedText value={veritasName} fontSize="12px" />
      </Flex>
    </UnstyledButton>
  );
}

export default function AntibodyFormatCreationPane({
  onRequestClose,
  onCreate,
  analyticsMetadata,
  existingFormat,
  onDisplayNameUpdated,
}: AntibodyFormatCreationPaneProps): JSX.Element {
  const isEditMode = existingFormat != null;
  const [formatName, setFormatName] = useState(isEditMode ? existingFormat.name : '');
  const [veritasName, setVeritasName] = useState(
    isEditMode ? (existingFormat.originalVeritas ?? existingFormat.name) : ''
  );
  const [previewName, setPreviewName] = useState<string | null>(
    isEditMode ? (existingFormat.originalVeritas ?? existingFormat.name) : null
  );
  const [isSaving, setIsSaving] = useState(false);

  const {status: previewStatus, result: previewResult} = useVeritasFormatPreviewQuery(previewName);

  const handleCreate = useCallback(async (): Promise<void> => {
    setIsSaving(true);

    try {
      const result = await AntibodyFormatDAO.createAntibodyFormat(
        {
          veritasName: veritasName.trim(),
          ...(formatName.trim() && {displayName: formatName.trim()}),
        },
        analyticsMetadata
      );
      await onCreate?.(result.antibodyFormatId);
    } finally {
      setIsSaving(false);
    }
  }, [veritasName, formatName, onCreate, analyticsMetadata]);

  const handleSaveDisplayName = useCallback(async (): Promise<void> => {
    if (existingFormat == null) {
      return;
    }
    setIsSaving(true);
    try {
      await AntibodyFormatDAO.updateAntibodyFormatDisplayName(
        existingFormat.id,
        formatName.trim(),
        analyticsMetadata
      );
      notifications.success('Display name updated successfully.');
      await onDisplayNameUpdated?.();
      onRequestClose();
    } catch (e) {
      notifications.error('Failed to update display name.');
      throw e;
    } finally {
      setIsSaving(false);
    }
  }, [analyticsMetadata, existingFormat, formatName, onDisplayNameUpdated, onRequestClose]);

  const handlePreview = (): void => {
    setPreviewName(veritasName.trim());
  };

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && veritasName.trim().length > 0) {
      handlePreview();
    }
  };

  const handleExampleFormatClick = (name: string): void => {
    setVeritasName(name);
    setPreviewName(name);
  };

  const handleClear = (): void => {
    setVeritasName('');
    setPreviewName(null);
  };

  const handleOpenHelpGuide = (): void => {
    legacyRunModal(VeritasHelpGuideModal, {});
  };

  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'AntibodyFormatCreationPane', element: 'root'})}
    >
      <div className={css(styles.header)}>
        <div className={css(styles.headerTitle)}>
          {isEditMode ? 'Edit display name' : 'Create new protein format'}
        </div>
        <div className={css(styles.headerButtons)}>
          <Button
            onClick={onRequestClose}
            text="Cancel"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
          />
          <Button
            onClick={isEditMode ? handleSaveDisplayName : handleCreate}
            text={isSaving ? (isEditMode ? 'Saving...' : 'Creating...') : isEditMode ? 'Save' : 'Create'}
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.SMALL}
            disabled={
              isEditMode
                ? isSaving || formatName.trim().length === 0
                : !canUseBiologicsFeature(BiologicsFeature.BIOLOGICS_CREATE) ||
                  veritasName.trim().length === 0 ||
                  isSaving
            }
            dataTestAttrs={{
              component: 'AntibodyFormatCreationPane',
              element: isEditMode ? 'saveButton' : 'createButton',
            }}
          />
        </div>
      </div>
      <Divider />
      <Grid gutter="md">
        <Grid.Col span={6} p="xl">
          <Flex justify="space-between" align="center" mb="12px" gap="8px">
            <Text className={css(styles.veritasHeader)}>
              {isEditMode
                ? 'Update the display name for this format'
                : 'Use VERITAS nomenclature to define a custom format'}
            </Text>
            {!isEditMode && (
              <Button
                onClick={handleOpenHelpGuide}
                text="How to use VERITAS"
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.SMALL}
                className={css(styles.howToUseVeritasButton)}
              />
            )}
          </Flex>
          <FormGroup>
            <Group align="center">
              <FieldLabel
                label="VERITAS name"
                htmlFor="veritas-name"
                isRequired={!isEditMode}
                className={css(styles.veritasNameLabel)}
              />
              {!isEditMode && (
                <Box mb="4px">
                  <Popover
                    placement={FloatingPlacement.BOTTOM_START}
                    renderContent={() => <VeritasSyntaxKey />}
                    dataTestAttrs={{
                      component: 'AntibodyFormatCreationPane',
                      element: 'highlightLegendPopover',
                    }}
                  >
                    <Flex align="center" gap="6px">
                      <Text span={true} fz="md" color={Color.BLUE_400}>
                        <BenchlingIcon icon="more-info" />
                      </Text>
                      <Text span={true} fz="md" color={Color.GRAY_500}>
                        Highlighting legend
                      </Text>
                    </Flex>
                  </Popover>
                </Box>
              )}
            </Group>
            <VeritasHighlightedInput
              id="veritas-name"
              onChange={isEditMode ? undefined : setVeritasName}
              onKeyDown={isEditMode ? undefined : handleKeyPress}
              placeholder="Enter VERITAS name"
              value={veritasName}
              readOnly={isEditMode}
              disabled={isEditMode}
              onBlur={
                isEditMode
                  ? undefined
                  : () => {
                      analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.TYPED_CUSTOM_FORMAT_VERITAS_NAME, {
                        ...analyticsMetadata,
                        veritas_name: veritasName,
                      });
                    }
              }
            />
            {!isEditMode && (
              <div className={css(styles.exampleFormatsSection)}>
                <Text span={true} color="gray.7" mb="8px" display="block">
                  Examples:
                </Text>
                <Flex wrap="wrap" gap="8px">
                  {EXAMPLE_FORMATS.map((format) => (
                    <ExampleFormatCard
                      key={format}
                      veritasName={format}
                      onClick={() => {
                        handleExampleFormatClick(format);
                      }}
                    />
                  ))}
                </Flex>
              </div>
            )}
            <FieldLabel label="Display name" htmlFor="display-name" />
            <TextInput
              size="md"
              id="display-name"
              onChange={(e) => {
                setFormatName(e.target.value);
              }}
              placeholder="Enter name"
              value={formatName}
              onBlur={() => {
                analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.TYPED_CUSTOM_FORMAT_DISPLAY_NAME, {
                  ...analyticsMetadata,
                  display_name: formatName,
                });
              }}
            />
            {!isEditMode && (
              <Flex justify="flex-end" mt="12px" gap="8px">
                <Button
                  onClick={handleClear}
                  icon="close"
                  text="Clear"
                  buttonStyle={ButtonStyle.SECONDARY_WHITE}
                  size={ButtonSize.SMALL}
                  disabled={veritasName.trim().length === 0}
                />
                <Button
                  onClick={handlePreview}
                  icon="eye"
                  text="Preview"
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  size={ButtonSize.SMALL}
                  disabled={veritasName.trim().length === 0}
                  dataTestAttrs={{component: 'AntibodyFormatCreationPane', element: 'previewButton'}}
                />
              </Flex>
            )}
          </FormGroup>
        </Grid.Col>
        <Grid.Col span={6} p="xl">
          <div className={css(styles.previewPanel)}>
            {previewName == null ? (
              <div className={css(styles.previewPlaceholder)}>
                Enter VERITAS name to preview protein format
              </div>
            ) : (
              <>
                {previewStatus === QueryResultType.PENDING && <CenteredSpinner />}
                {previewStatus === QueryResultType.ERROR && (
                  <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to preview format." />
                )}
                {previewStatus === QueryResultType.SUCCESS &&
                  previewResult != null &&
                  previewResult.__typename === 'VeritasFormatPreviewErrorNode' && (
                    <InlineBanner
                      bannerType={InlineBannerType.ERROR}
                      text={`Unable to preview format: ${previewResult.message}`}
                    />
                  )}
                {previewStatus === QueryResultType.SUCCESS &&
                  previewResult != null &&
                  previewResult.__typename === 'AntibodyFormatNode' && (
                    <div
                      {...getTestProps({
                        component: 'AntibodyFormatCreationPane',
                        element: 'preview-container',
                      })}
                    >
                      <div className={css(styles.chainLegendWrapper)}>
                        <AntibodyChainLegend antibodyFormat={previewResult} />
                      </div>
                      <Antibody format={previewResult} showLabels={true} showPopovers={true} />
                    </div>
                  )}
              </>
            )}
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    height: '52px',
  },
  headerTitle: {
    ...FONT_PROXIMA_16,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  headerButtons: {
    display: 'flex',
    gap: '12px',
  },
  veritasHeader: {
    ...FONT_PROXIMA_12,
    color: Color.GRAY_900,
  },
  howToUseVeritasButton: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  veritasNameLabel: {
    flexGrow: 1,
  },
  exampleFormatsSection: {
    ...FONT_PROXIMA_12,
    marginTop: '8px',
    marginBottom: '12px',
  },
  previewPanel: {
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    backgroundColor: Color.GRAY_50,
    padding: '8px',
    minHeight: '304px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewPlaceholder: {
    ...FONT_PROXIMA_12,
    color: Color.GRAY_600,
    textAlign: 'center',
  },
  chainLegendWrapper: {
    backgroundColor: Color.WHITE,
    ...FONT_PROXIMA_12,
  },
});
